import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileSignature, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import SignatureCanvas from "../components/SignatureCanvas";

export default function FieldSignOff() {
  const { blockId } = useParams();
  const nav = useNavigate();
  const sigRef = useRef(null);
  const [block, setBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: md } = await api.get("/api/field/my-day");
        if (cancelled) return;
        const b = (md.blocks || []).find((x) => x.id === blockId);
        setBlock(b || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [blockId]);

  const canSubmit = name.trim().length > 0 && acknowledged && !busy && !!block;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const sigData = sigRef.current?.toDataURL();
      await api.post("/api/field/completions", {
        block_id: blockId,
        project_id: block.project_id,
        customer_name_typed: name.trim(),
        acknowledged: true,
        signature_data_url: sigData || null,
        notes: notes.trim() || null,
      });
      toast.success("Sign-off captured");
      nav(`/jobs/${blockId}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "completion_photo_required") {
        toast.error(
          `${detail.required} completion photo required (you have ${detail.current_count}). Tap "Back to job" and add one in the Operational Evidence panel.`,
          { duration: 7000 }
        );
      } else {
        toast.error(formatApiError(detail) || e.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="field-signoff-page" className="px-4 pt-4 pb-6 max-w-[640px] mx-auto">
      <Link to={`/jobs/${blockId}`} className="inline-flex items-center gap-1 text-[12px] font-bold"
            style={{ color: "var(--ink-muted)" }} data-testid="signoff-back">
        <ArrowLeft size={13} /> Back to job
      </Link>

      <div className="flex items-center gap-2 mt-3 mb-1">
        <FileSignature size={14} style={{ color: "var(--accent)" }} />
        <span className="text-[10.5px] font-extrabold uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>
          Customer sign-off
        </span>
      </div>
      <h1 className="text-[22px] font-extrabold leading-tight" style={{ color: "var(--ink)" }}>
        Job complete?
      </h1>
      <p className="text-[12px] mt-1.5" style={{ color: "var(--ink-soft)" }}>
        Customer reviews the finished work and confirms acceptance. This becomes part of the project's evidence pack.
      </p>

      {loading && (
        <div data-testid="signoff-loading" className="mt-5 text-[12px]" style={{ color: "var(--ink-muted)" }}>
          Loading job…
        </div>
      )}

      {!loading && !block && (
        <div
          data-testid="signoff-not-in-day"
          className="mt-5 px-4 py-6 rounded-md text-center"
          style={{ background: "var(--bg-elev-1)", border: "1px dashed var(--line-strong)" }}
        >
          <div className="text-[13px] font-bold" style={{ color: "var(--ink)" }}>
            This job isn't on today's list.
          </div>
          <div className="text-[11.5px] mt-1.5" style={{ color: "var(--ink-muted)" }}>
            Open it from My Day or contact your supervisor.
          </div>
          <Link to="/" className="inline-block mt-3 px-3 py-2 rounded-md text-[11.5px] font-extrabold uppercase tracking-[0.10em]"
                style={{ background: "var(--brand)", color: "#fff" }} data-testid="back-to-my-day-empty">
            Back to My Day
          </Link>
        </div>
      )}

      {!loading && block && (
        <div className="mt-5 space-y-4">
          <div data-testid="signoff-name-wrap">
            <Label>Customer name (typed)</Label>
            <input
              data-testid="signoff-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Tom Mitchell"
              className="w-full"
              style={{ minHeight: 48 }}
            />
          </div>

          <div data-testid="signoff-signature-wrap">
            <Label optional>Touch signature (optional but recommended)</Label>
            <SignatureCanvas ref={sigRef} height={200} />
          </div>

          <div data-testid="signoff-notes-wrap">
            <Label optional>Notes</Label>
            <textarea
              data-testid="signoff-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any defects called out, follow-ups agreed…"
              className="w-full"
            />
          </div>

          <label
            className="flex items-start gap-3 p-3 rounded-md cursor-pointer"
            data-testid="signoff-ack-wrap"
            style={{
              background: acknowledged ? "rgba(70,40,140,0.06)" : "var(--bg-elev-1)",
              border: "1px solid " + (acknowledged ? "var(--brand)" : "var(--line-strong)"),
            }}
          >
            <input
              type="checkbox"
              data-testid="signoff-ack"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-[2px]"
              style={{ width: 18, height: 18 }}
            />
            <div className="text-[12px] leading-snug" style={{ color: "var(--ink)" }}>
              I, the customer, confirm the work is complete to my satisfaction and authorise Manchester Roofing Australia to invoice for the agreed scope and any approved variations.
            </div>
          </label>

          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="signoff-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-4 rounded-md text-[13px] font-extrabold uppercase tracking-[0.12em] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "var(--brand)", color: "#fff", minHeight: 56 }}
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
            Capture sign-off
          </button>
          {!canSubmit && !busy && (
            <div className="text-[11px] text-center" style={{ color: "var(--ink-muted)" }} data-testid="signoff-validation-hint">
              {name.trim().length === 0 ? "Type the customer's name to continue."
               : !acknowledged ? "Tick the acknowledgement box to continue." : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Label({ children, optional }) {
  return (
    <div className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] mb-1.5"
         style={{ color: "var(--ink-muted)" }}>
      {children} {optional && <span style={{ color: "var(--ink-faint)" }}>· optional</span>}
    </div>
  );
}
